import { useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback, useState } from 'react'
import { Card, Text, useTheme } from 'react-native-paper'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { useDeleteJob, useJobHasTimeEntries } from '@/hooks/jobs'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useJobForm } from '@/hooks/useJobForm'
import { useJobRecord } from '@/hooks/useJobRecord'
import { useUpdateJob } from '@/hooks/useUpdateJob'
import { handleMutationErrorsWithForm } from '@/lib/errors'
import { notify } from '@/lib/notice'
import { FormErrorSummary } from '@/ui/form'
import { DeleteJobDialog } from '@/ui/jobs/DeleteJobDialog'
import { JobFormFields } from '@/ui/jobs/JobFormFields'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { Page } from '@/ui/Page'

export default function JobEditPage() {
    useAvoidSoftInputForScreen()
    const router = useRouter()
    const params = useLocalSearchParams<{ orgSlug: string; jobSlug: string }>()
    const _theme = useTheme()
    const updateJob = useUpdateJob(params.orgSlug)

    const { isLoading, job, customer } = useJobRecord(params.jobSlug)
    const { mutation: deleteMutation } = useDeleteJob()
    const { hasTimeEntries } = useJobHasTimeEntries(job?.id)

    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deleteError, setDeleteError] = useState<string | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const {
        control,
        handleSubmit,
        formState: { isSubmitting, errors, isSubmitted, isDirty },
        setError,
        getValues,
        setValue,
        onSubmitRef,
    } = useJobForm({
        job,
        customer,
        tags: job?.tags,
        address: job?.expand?.address,
    })

    const handleUpdateJob = async (data: Parameters<typeof updateJob.mutateAsync>[0]) => {
        try {
            await updateJob.mutateAsync(data)
        } catch (error) {
            handleMutationErrorsWithForm({ setError, getValues })(error)
        }
    }

    onSubmitRef.current = handleUpdateJob

    const handleDelete = useCallback(() => {
        setDeleteError(null)
        setDeleteDialogOpen(true)
    }, [])

    const handleConfirmDelete = async () => {
        if (!job) return
        setIsDeleting(true)
        try {
            setDeleteError(null)
            await deleteMutation.mutateAsync(job.id)

            setDeleteDialogOpen(false)
            notify.success('Job deleted successfully')
            router.replace(`/${params.orgSlug}/settings/jobs`)
        } catch (err) {
            const raw = err instanceof Error ? err.message : 'Failed to delete job'
            const hasTimeEntries = raw.includes('time') || raw.includes('relation reference')
            setDeleteError(
                hasTimeEntries
                    ? 'This job cannot be deleted because people have logged time against it. Remove all time entries for this job first.'
                    : raw
            )
        } finally {
            setIsDeleting(false)
        }
    }

    useHeaderOptions({
        buttons: [
            {
                type: 'delete',
                onPress: handleDelete,
                disabled: isSubmitting || isDeleting,
                loading: isDeleting,
                loadingTitle: 'Deleting...',
            },
            {
                type: 'save',
                onPress: handleSubmit,
                loading: isSubmitting,
                loadingTitle: 'Saving...',
            },
        ],
        title: job ? `Edit ${job.name || job.identifier}` : undefined,
        smallScreenTitle: 'Edit',
        enabled: !!job,
    })

    if (isLoading) {
        return <LoadingIndicator />
    }

    if (!job) {
        return (
            <Page>
                <Card mode="outlined" contentStyle={{ padding: 16 }}>
                    <Text>
                        Job not found. It may have been deleted or you don't have permission to edit
                        it.
                    </Text>
                </Card>
            </Page>
        )
    }

    return (
        <Page>
            <FormErrorSummary errors={errors} isEnabled={isSubmitted} />

            <JobFormFields control={control} setValue={setValue} />
            <UnsavedChangesDialog isDirty={isDirty} />
            <DeleteJobDialog
                visible={isDeleteDialogOpen}
                jobName={job.name || job.identifier}
                hasTimeEntries={hasTimeEntries}
                onDismiss={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeleting}
                errorMessage={deleteError}
            />
        </Page>
    )
}
